/* eslint-disable react-refresh/only-export-components -- noddiButtonVariants (cva) is shared with NoddiDummyButton; edits here full-reload instead of hot-swap */
import { cn, cva, NoddiIcon, NoddiIconName, type VariantProps } from 'noddi-ui-common';
import { Slot as SlotPrimitive } from 'radix-ui';
import { ButtonHTMLAttributes, forwardRef, useId } from 'react';
import { webButtonStyling } from '../../helpers/webButtonStyling';
import { NoddiSpinner } from '../Feedback/NoddiSpinner';

// Don't add hover as effect persists on mobile after pressed, which makes the button look disabled. ( Can do for over md > )
export const noddiButtonVariants = cva(
  cn(
    'flex size-fit shrink-0 items-center justify-center gap-2 text-sm font-medium text-nowrap disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*="size-"])]:size-4',
    webButtonStyling
  ),
  {
    variants: {
      variant: {
        primary: cn('border border-button-primary bg-button-primary text-button-primary-foreground'),
        secondary: cn('bg-secondary text-secondary-foreground shadow-xs md:hover:bg-secondary-hover'),
        outline: cn(
          'border border-input bg-background text-foreground shadow-xs md:hover:bg-accent md:hover:text-accent-foreground'
        ),
        ghost: cn(''),
        subtle: cn('bg-gray-100 text-gray-600 hover:bg-gray-200'),
        success: cn('border border-success bg-success text-white'),
        destructive: cn('border border-destructive bg-destructive text-destructive-foreground'),
        // Low-emphasis destructive: reads as an outline button but marks the action as dangerous.
        destructiveOutline: cn('border border-destructive bg-background text-destructive shadow-xs'),
        dark: cn('border border-black bg-black text-white'),
        // Navigation-looking text, not an action: an onClick action (split, remove, edit, show
        // more) is `outline`, `small` in dense rows. Prefer `NoddiLink variant='link'` for links.
        link: cn('text-primary underline underline-offset-4'),
        // Sets no color: preflight gives `button`/`a` `color: inherit`, so it takes whatever
        // surface it is dropped on. Naming a foreground token here shipped 1.46:1 on carglass.
        lightLink: cn('underline underline-offset-4')
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        small: 'h-8 gap-1.5 px-3 has-[>svg]:px-2.5',
        extraSmall: 'h-7 gap-1.5 px-2.5 has-[>svg]:px-2'
      }
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default'
    }
  }
);

export type NoddiButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof noddiButtonVariants> & {
    loading?: boolean;
    asChild?: boolean;
    /**
     * Why the action is unavailable, shown under the button. Setting it disables the button, so a
     * disabled action always says why.
     */
    disabledReason?: string;
  } & ({ startIcon?: NoddiIconName; endIcon?: never } | { endIcon?: NoddiIconName; startIcon?: never });

export const NoddiButton = forwardRef<HTMLButtonElement, NoddiButtonProps>(
  (
    {
      className,
      variant,
      size,
      endIcon,
      startIcon,
      loading,
      disabled,
      disabledReason,
      children,
      asChild = false,
      ...props
    },
    ref
  ) => {
    const reasonId = useId();
    const buttonClassName = cn(noddiButtonVariants({ variant, size }), className);

    const Comp = asChild ? SlotPrimitive.Slot : 'button';

    const button = (
      <Comp
        className={buttonClassName}
        type={!asChild ? 'button' : undefined}
        ref={ref}
        disabled={disabled || loading || !!disabledReason}
        {...props}
        aria-describedby={disabledReason ? reasonId : props['aria-describedby']}
      >
        {/* Icon and spinner inherit the button's own text color, so a `className` that
            recolors the label (e.g. `text-destructive`) can never leave them behind. */}
        {startIcon && !loading && <NoddiIcon name={startIcon} />}
        {loading && !endIcon && <NoddiSpinner className='text-current' size='small' />}

        {asChild ? (
          <SlotPrimitive.Slottable>{children}</SlotPrimitive.Slottable>
        ) : (
          <span className='min-w-0 text-left'>{children}</span>
        )}

        {loading && endIcon && <NoddiSpinner className='text-current' size='small' />}
        {endIcon && !loading && <NoddiIcon name={endIcon} />}
      </Comp>
    );

    if (!disabledReason) {
      return button;
    }

    return (
      <span className='flex w-fit flex-col items-start gap-1'>
        {button}
        <span id={reasonId} className='text-xs text-muted-foreground'>
          {disabledReason}
        </span>
      </span>
    );
  }
);
NoddiButton.displayName = 'NoddiButton';
